import { isApp, networkStatus, GCS_ROOT_PATH } from '@/index'
import { VoiceFile } from '../data/types'
import { Filesystem, Directory } from '@capacitor/filesystem'
import { Capacitor } from '@capacitor/core'
import { CloudFile } from '@/lib/CloudFile'
import { ref } from 'vue'

// 캐시 디렉토리
const STORAGE_DIRECTORY = Directory.Cache

// 전역 재생 상태 관리
type VoiceId = string | number
export const currentPlayingVoiceId = ref<VoiceId | null>(null)

// 재생 중지 요청 이벤트 (다른 음성 재생 시 기존 음성 중지용)
type StopCallback = () => void
const stopCallbacks = new Map<VoiceId, StopCallback>()

export function registerVoiceStopCallback(voiceId: VoiceId, callback: StopCallback): void {
  stopCallbacks.set(voiceId, callback)
}

export function unregisterVoiceStopCallback(voiceId: VoiceId): void {
  stopCallbacks.delete(voiceId)
}

export function stopOtherVoices(exceptVoiceId: VoiceId): void {
  stopCallbacks.forEach((callback, id) => {
    if (id !== exceptVoiceId) {
      callback()
    }
  })
}

export function setCurrentPlayingVoice(voiceId: VoiceId | null): void {
  currentPlayingVoiceId.value = voiceId
}

// 음성 파일 기본 폴더명
const VOICE_FILES_BASE_FOLDER = 'voice'

// 경로 추출
export function getVoiceFilePath(voiceFile: VoiceFile): string {
  const url = voiceFile.url

  // local-file:// URL인 경우
  if (url.startsWith('local-file://')) {
    const match = url.match(/Documents\/(.+)$/)
    if (match) return match[1]
    return url.replace('local-file://', '')
  }

  if (!url.startsWith('storage://')) return url
  // storage://homecheck-service/ 제거
  return url.replace(/^storage:\/\/[^/]+\//, '')
}

// 경로 생성
export function buildVoiceFilePath(params: {
  taskId: number
  facilityId: number
  floorplanId?: number | null
  defectId?: number | null
  templateId?: number | null
  recordItemKey?: string
  fileName: string
}): string {
  const parts = [
    GCS_ROOT_PATH,
    VOICE_FILES_BASE_FOLDER,
    `task/${params.taskId}`,
    `facility/${params.facilityId}`,
  ]

  // 결함
  if (params.floorplanId) {
    parts.push(`floorplan/${params.floorplanId}`)
    if (params.defectId) {
      parts.push(`defect/${params.defectId}`)
    }
  }
  // 보고서
  else if (params.templateId) {
    parts.push(`template/${params.templateId}`)
    if (params.recordItemKey) {
      parts.push(`record_item_key/${params.recordItemKey}`)
    }
  }

  parts.push(params.fileName)
  return parts.join('/')
}

// 로컬 파일 존재 여부 확인
export async function checkLocalVoiceFileExists(voiceFile: VoiceFile): Promise<boolean> {
  if (!isApp()) return false
  try {
    const filePath = getVoiceFilePath(voiceFile)
    await Filesystem.stat({
      path: filePath,
      directory: STORAGE_DIRECTORY,
    })
    return true
  } catch {
    return false
  }
}

// 음성 파일 재생 가능 여부 확인
export async function checkVoiceFilePlayable(voiceFile: VoiceFile): Promise<boolean> {
  // 온라인이면 재생 가능
  if (networkStatus.value.status) return true
  // 오프라인이면 로컬 파일 존재 여부 확인
  return await checkLocalVoiceFileExists(voiceFile)
}

// 로컬에 다운로드
export async function downloadLocalVoiceFile(voiceFile: VoiceFile): Promise<void> {
  if (!isApp()) return

  // local-file:// URL은 이미 로컬 파일이므로 다운로드 불필요
  if (voiceFile.url.startsWith('local-file://')) return

  try {
    const filePath = getVoiceFilePath(voiceFile)

    // 이미 파일이 존재하는지 확인
    try {
      await Filesystem.stat({
        path: filePath,
        directory: STORAGE_DIRECTORY,
      })
      return
    } catch {
      // 에러핸들링 x
    }

    // storage:// URL을 signed URL로 변환
    const signedUrl = CloudFile.ResourceUrlToSignedUrl(voiceFile.url)

    // URL에서 파일 다운로드
    const response = await fetch(signedUrl)
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    const blob = await response.blob()

    // Blob을 base64로 변환
    const reader = new FileReader()

    return new Promise((resolve, reject) => {
      reader.onloadend = async (): Promise<void> => {
        try {
          const base64Data = (reader.result as string).split(',')[1]

          // 파일 저장
          await Filesystem.writeFile({
            path: filePath,
            data: base64Data,
            directory: STORAGE_DIRECTORY,
            recursive: true,
          })

          resolve()
        } catch (error) {
          console.error('[downloadLocalVoiceFile] Error saving file:', error)
          reject(error)
        }
      }
      reader.onerror = (error): void => {
        console.error('[downloadLocalVoiceFile] FileReader error:', error)
        reject(error)
      }
      reader.readAsDataURL(blob)
    })
  } catch (error) {
    console.error('[downloadLocalVoiceFile] Error:', error)
    throw error
  }
}

// 재생 가능한 url 추출
export async function getPlayableUrl(voiceFile: VoiceFile): Promise<string> {
  // 웹은 signed URL 반환
  if (!isApp()) {
    return CloudFile.ResourceUrlToSignedUrl(voiceFile.url)
  }

  // local-file:// URL인 경우
  if (voiceFile.url.startsWith('local-file://')) {
    const filePath = getVoiceFilePath(voiceFile)

    try {
      // Capacitor 파일 URI 얻기
      const result = await Filesystem.getUri({
        path: filePath,
        directory: STORAGE_DIRECTORY,
      })

      // iOS: Native Audio Player는 file:// URL 직접 사용
      if (Capacitor.getPlatform() === 'ios') {
        return result.uri
      }

      // Android: capacitor:// URL로 변환 (WebView Audio 지원)
      return Capacitor.convertFileSrc(result.uri)
    } catch (error) {
      console.error('[getPlayableUrl] local-file URL error:', error)
      throw error
    }
  }

  // storage:// URL을 signed URL로 변환
  const signedUrl = CloudFile.ResourceUrlToSignedUrl(voiceFile.url)

  // 앱 환경: 로컬 파일 우선, 없으면 signed URL
  try {
    const filePath = getVoiceFilePath(voiceFile)

    // 파일 존재 확인
    await Filesystem.stat({
      path: filePath,
      directory: STORAGE_DIRECTORY,
    })

    // Capacitor 파일 URI 얻기
    const result = await Filesystem.getUri({
      path: filePath,
      directory: STORAGE_DIRECTORY,
    })

    // iOS: Native Audio Player는 file:// URL 직접 사용
    if (Capacitor.getPlatform() === 'ios') {
      return result.uri
    }

    // Android: capacitor:// URL로 변환 (WebView Audio 지원)
    return Capacitor.convertFileSrc(result.uri)
  } catch {
    // 로컬 파일이 없으면 signed URL 반환
    return signedUrl
  }
}

// 다운로드 (웹)
export async function downloadVoiceFile(voiceFile: VoiceFile): Promise<void> {
  try {
    const file = await CloudFile.downloadFileFromResourceUrl(voiceFile.url)
    if (!file) {
      throw new Error('Failed to download file')
    }

    const extension = voiceFile.url.split('.').pop() || 'mp3'
    const fileName = `${voiceFile.name}.${extension}`

    const blobUrl = window.URL.createObjectURL(file)
    const link = document.createElement('a')
    link.href = blobUrl
    link.download = fileName
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(blobUrl)
  } catch (error) {
    console.error('Failed to download voice file:', error)
    throw error
  }
}

// ms를 mm:ss 포맷으로 변환
export function formatMsToMMSS(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return '00:00'
  const s = Math.floor(ms / 1000)
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`
}

// 녹음한 음성 파일 로컬에 저장
export async function saveVoiceFile(base64Data: string, filePath: string): Promise<string> {
  if (!isApp()) return filePath

  try {
    // base64 데이터 정제 (data URI 프리픽스 제거)
    let cleanBase64 = base64Data
    if (base64Data.includes(',')) {
      cleanBase64 = base64Data.split(',')[1]
    }

    // 디렉토리 경로 추출 (파일명 제외)
    const pathParts = filePath.split('/')
    const dirPath = pathParts.slice(0, -1).join('/')

    // 디렉토리 생성 (없으면 생성)
    if (dirPath) {
      try {
        await Filesystem.mkdir({
          path: dirPath,
          directory: STORAGE_DIRECTORY,
          recursive: true,
        })
      } catch {
        // 이미 존재하면 에러 무시
      }
    }

    await Filesystem.writeFile({
      path: filePath,
      data: cleanBase64,
      directory: STORAGE_DIRECTORY,
    })

    return `local-file:///storage/emulated/0/Documents/${filePath}`
  } catch (error) {
    console.error('[saveNewVoiceFileLocally] Error:', error)
    throw error
  }
}

/**
 * 중복되지 않는 음성 파일명 생성 (번호 자동 증가)
 * @param baseFileName - 기본 파일명 (예: '템플릿명')
 * @param existingFiles - 같은 facility/template의 기존 파일들
 * @returns 번호가 포함된 파일명 (예: '템플릿명 1')
 */
export function getNextVoiceFileName(baseFileName: string, existingFiles: VoiceFile[]): string {
  // 기존 파일명에서 번호 추출하여 가장 큰 번호 찾기
  let maxNumber = 0
  const escapedBaseName = baseFileName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const pattern = new RegExp(`^${escapedBaseName} (\\d+)$`)

  for (const file of existingFiles) {
    const match = file.name.match(pattern)
    if (match) {
      const num = parseInt(match[1])
      if (num > maxNumber) maxNumber = num
    }
  }

  return `${baseFileName} ${maxNumber + 1}`
}

/**
 * 로컬 파일 경로에서 파일을 읽어 File 객체로 변환
 * @param localFilePath - local-file:// 형식의 경로
 * @param mimeType - MIME 타입 (기본: audio/mp4)
 * @returns File 객체
 */
export async function readLocalVoiceFileAsFile(
  localFilePath: string,
  mimeType = 'audio/mp4',
): Promise<File> {
  // local-file:// 프리픽스 제거하고 실제 경로 추출
  let filePath = localFilePath
  if (localFilePath.startsWith('local-file://')) {
    // local-file:///storage/emulated/0/Documents/voice/... -> voice/...
    filePath = localFilePath.replace('local-file:///storage/emulated/0/Documents/', '')
  }

  // Filesystem API로 파일 읽기
  const result = await Filesystem.readFile({
    path: filePath,
    directory: STORAGE_DIRECTORY,
  })

  // base64를 File로 변환
  const base64Data = result.data as string
  return convertBase64ToFile(base64Data, mimeType)
}

// base64 문자열을 File 객체로 변환 (동기 처리)
export function convertBase64ToFile(base64Data: string, mimeType = 'audio/mp4'): File {
  // data URI 프리픽스 제거
  let cleanBase64 = base64Data
  if (base64Data.includes(',')) {
    cleanBase64 = base64Data.split(',')[1]
  }

  // base64를 binary로 디코딩
  const binaryString = atob(cleanBase64)
  const bytes = new Uint8Array(binaryString.length)

  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i)
  }

  // Blob 생성 후 File로 변환
  const blob = new Blob([bytes], { type: mimeType })
  const fileName = `voice_${Date.now()}.m4a`
  const file = new File([blob], fileName, { type: mimeType })

  return file
}
